import React, { useState, useRef } from 'react';
import { useTranslation } from '../i18n';
import Swal from 'sweetalert2';
import API_BASE_URL from '../config/api';

const FileUploadComponent = ({ onFileUpload, activityId, activityType }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const userId = localStorage.getItem('userId');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile || !selectedFile.name.endsWith('.fit')) {
      setError(t('fileUpload.onlyFitFiles'));
      setFile(null);
      return;
    }
    setFile(selectedFile);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError(t('fileUpload.pleaseSelectFile'));
      return;
    }

    if (!userId) {
      setError(t('fileUpload.noUserId'));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    formData.append('activityId', activityId);

    setUploading(true);
    setError(null);

    try {
      // 📤 1) Subir archivo .fit
      const response = await fetch(`${API_BASE_URL}/api/fit/upload`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al subir archivo');
      }

      if (!result.fileId) {
        throw new Error('El servidor no retornó un fileId. Verifica que el archivo sea válido.');
      }

      setUploadedFileId(result.fileId);

      // 🛠️ 2) Guardar el fileId en la actividad (opcional, si ya lo haces en backend podríamos saltarlo)
      const saveFileIdResponse = await fetch(
        `${API_BASE_URL}/api/activities/update/${activityId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            completed: {
              fileId: result.fileId
            }
          })
        }
      );

      if (!saveFileIdResponse.ok) {
        const errResult = await saveFileIdResponse.json();
        throw new Error(`Error guardando fileId en actividad: ${errResult.message}`);
      }

      // 🔁 3) Sincronizar datos del archivo con la actividad + disparar correo post-workout
      const updateResponse = await fetch(
        `${API_BASE_URL}/api/fit/sync-activity/${result.fileId}?resendMail=true`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,        // 👈 nombre que espera el backend
            activityId,    // 👈 lo usará para findById
            activityType   // 👈 opcional, pero ayuda
          })
        }
      );

      if (!updateResponse.ok) {
        const updateResult = await updateResponse.json();
        console.error('❌ Error actualizando actividad:', updateResult.message);
      } else {
        Swal.fire({
          icon: 'success',
          title: t('fileUpload.activityUpdated'),
          text: t('fileUpload.syncSuccess'),
          timer: 3000,
          showConfirmButton: false
        });
      }

      if (onFileUpload) {
        onFileUpload({ fileId: result.fileId });
      }

    } catch (err) {
      console.error('🔥 Error en fetch:', err);
      setError(err.message || 'Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
      <input
        type="file"
        accept=".fit"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <button
        onClick={triggerFileSelect}
        className="btn"
        style={{ backgroundColor: '#193255', color: '#fff' }}
      >
        {t('fileUpload.selectFile')}
      </button>

      <button
        onClick={handleUpload}
        className="btn btn-primary"
        disabled={uploading || !file}
      >
        {uploading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            {t('fileUpload.uploading')}
          </>
        ) : t('fileUpload.upload')}
      </button>

      {error && <div className="text-danger ms-2">{error}</div>}
      {uploadedFileId && (
        <div className="text-success ms-2">
          ✅ Archivo subido: {uploadedFileId}
        </div>
      )}
    </div>
  );
};

export default FileUploadComponent;
